import { useEffect, useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Big } from 'big.js'
import { motion, MotionProps } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { z } from 'zod'

import { Currency } from '@/constants/currency'
import { queryKeys } from '@/constants/queryKeys'
import { getFXRates } from '@/domains/Business/api'
import { formatCurrency, formatRate } from '@/lib/money'
import { cn } from '@/lib/utils'
import { AmountInput } from '@/shared/components'
import { ArrowDown, ArrowTrendUp } from '@/shared/icons/outline'
import {
  Button,
  Form,
  FormField,
  FormItem,
  Sheet,
  SheetContent,
  Skeleton,
  Typography,
} from '@/shared/ui'

const exchangeSchema = z.object({
  amountIn: z.string(),
  amountOut: z.string(),
  currencyIn: z.nativeEnum(Currency),
  currencyOut: z.nativeEnum(Currency),
})

const ANIMATION_CONFIG: MotionProps['transition'] = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
  scale: {
    times: [0, 0.5, 1],
    duration: 0.4,
  },
}

const TOP_INPUT_POSITION = 70
const BOTTOM_INPUT_POSITION = 0
const POSITION_DELTA = 70
const SCALE_IN = [1, 1.15, 1]
const SCALE_OUT = [1, 1.14, 1]

type ExchangeSchema = z.infer<typeof exchangeSchema>

type Props = {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  currencyIn: Currency
  currencyOut: Currency
}

export const CalculateRateSidebar = ({
  isOpen,
  onOpenChange,
  currencyIn = Currency.USDC,
  currencyOut = Currency.MXN,
}: Props) => {
  const [swapRates, setSwapRates] = useState(false)
  const intl = useIntl()

  const form = useForm<ExchangeSchema>({
    mode: 'onChange',
    resolver: zodResolver(exchangeSchema),
    values: {
      currencyIn,
      currencyOut,
      amountIn: '',
      amountOut: '',
    },
  })

  const ratesQuery = useQuery({
    queryKey: [queryKeys.getFXRates, currencyIn, currencyOut],
    queryFn: () =>
      getFXRates({
        from: currencyIn,
        to: currencyOut,
      }),
    select: (data) => data.data,
    refetchInterval: 10 * 1000,
    enabled: isOpen,
  })

  const currentRate = useMemo(() => {
    if (ratesQuery.data) {
      const { askRate, bidRate } = ratesQuery.data

      return swapRates ? bidRate : askRate
    }

    return 1
  }, [ratesQuery.data, swapRates])

  useEffect(() => {
    const amountIn = form.getValues('amountIn')

    if (ratesQuery.data && amountIn !== '') {
      form.setValue(
        'amountOut',
        new Big(amountIn).times(currentRate).toFixed(2),
      )
    }
  }, [currentRate, form, ratesQuery.data, swapRates])

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent>
        <Typography variant="h3">
          <FormattedMessage
            id="label.exchangeCalculator"
            defaultMessage="Exchange calculator"
          />
        </Typography>

        <div className="p-1" />

        <div className="flex w-full items-center gap-1">
          <ArrowTrendUp />
          <div className="flex items-center gap-1">
            <Typography>1 {formatCurrency(currencyIn)} =</Typography>
            {ratesQuery.isFetching ? (
              <Skeleton className="h-[21px] w-24 rounded-md" />
            ) : (
              <Typography>
                {formatRate(currentRate)} {formatCurrency(currencyOut)}
              </Typography>
            )}
          </div>
        </div>

        <div className="p-4" />

        <Form {...form}>
          <form className="w-full">
            <motion.div
              initial={false}
              key="amountIn"
              animate={{
                y: swapRates
                  ? TOP_INPUT_POSITION
                  : TOP_INPUT_POSITION - POSITION_DELTA,
                scale: swapRates ? SCALE_IN : SCALE_OUT,
                transition: ANIMATION_CONFIG,
              }}
            >
              <FormField
                control={form.control}
                name="amountIn"
                render={({ field }) => (
                  <FormItem>
                    <AmountInput
                      currency={form.watch('currencyIn')}
                      placeholder={
                        swapRates
                          ? intl.formatMessage({
                              id: 'label.buy',
                              defaultMessage: 'Buy',
                            })
                          : intl.formatMessage({
                              id: 'label.sell',
                              defaultMessage: 'Sell',
                            })
                      }
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value)

                        if (value === '') {
                          form.setValue('amountOut', '')
                          return
                        }

                        const amountOut = swapRates
                          ? Big(value).div(currentRate).toFixed(2)
                          : Big(value).times(currentRate).toFixed(2)

                        form.setValue('amountOut', amountOut, {
                          shouldValidate: true,
                        })
                      }}
                    />
                  </FormItem>
                )}
              />
            </motion.div>

            <div className="relative z-10 -mt-5 -mb-5 flex justify-center">
              <Button
                variant="ghost"
                type="button"
                size="inline"
                className={cn(
                  'border-neutral-gray-100 flex size-14 items-center justify-center rounded-full border-[8px] bg-white transition-all duration-500',
                  swapRates && 'rotate-360',
                )}
                aria-label={intl.formatMessage({
                  id: 'label.swapCurrencies',
                  defaultMessage: 'Swap currencies',
                })}
                onClick={() => {
                  setSwapRates((r) => !r)
                }}
              >
                <ArrowDown className="size-5" />
              </Button>
            </div>

            <motion.div
              initial={false}
              key="amountOut"
              animate={{
                y: swapRates
                  ? BOTTOM_INPUT_POSITION - POSITION_DELTA
                  : BOTTOM_INPUT_POSITION,
                scale: swapRates ? SCALE_IN : SCALE_OUT,
                transition: ANIMATION_CONFIG,
              }}
            >
              <FormField
                control={form.control}
                name="amountOut"
                render={({ field }) => (
                  <FormItem>
                    <AmountInput
                      currency={form.watch('currencyOut')}
                      placeholder={
                        swapRates
                          ? intl.formatMessage({
                              id: 'label.sell',
                              defaultMessage: 'Sell',
                            })
                          : intl.formatMessage({
                              id: 'label.buy',
                              defaultMessage: 'Buy',
                            })
                      }
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value)

                        if (value === '') {
                          form.setValue('amountIn', '')
                          return
                        }

                        const amountIn = swapRates
                          ? Big(value).div(currentRate).toFixed(2)
                          : Big(value).times(currentRate).toFixed(2)

                        form.setValue('amountIn', amountIn, {
                          shouldValidate: true,
                        })
                      }}
                    />
                  </FormItem>
                )}
              />
            </motion.div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
